import React, { useEffect, useState } from "react";
import Translate from "../components/Translate";
import { getPosts } from "../utils/networkApi";
import Loader from "../components/Loader";
import { useTranslation } from "react-i18next";
import { updateMetaTags } from "../utils/seo";
import PageBanner from "../components/PageBanner";

export default function ArchivePage() {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({});

  useEffect(() => {
    updateMetaTags(
      `${t('archive')} | ${t('site-title')}`,
      t('archive-desc'),
      t('archive-keywords')
    );
  }, [t, i18n.language]);

  async function fetchData(page = 1) {
    try {
      setLoading(true);
      const savedLang = await localStorage.getItem("appLang") || "en";
      const response = await getPosts(`allArchive-api?lang=${savedLang}&page=${page}`);
      setData(response.archive.data);
      setPagination({
        current_page: response.archive.current_page,
        last_page: response.archive.last_page,
      });
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error('Error fetching data:', error);
    }
  }

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <section className="w-full min-h-screen bg-primary-50/30 pb-16">
      {loading && <Loader />}

      <PageBanner
        title={<Translate text="archive" />}
        subtitle={<Translate text="archive-subtitle" />}
        badgeText={t('badge-publication-archive')}
      />

      {/* Content Wrapper with increased top spacing */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {data.map((job, idx) => (
            <article
              key={`${job.id}-${idx}`}
              className="bg-white rounded-[24px] overflow-hidden shadow-sm hover:shadow-md border border-[#dadce0] transition flex flex-col group"
            >
              <div className="relative w-full h-52 overflow-hidden">
                <img
                  src={(job.image && typeof job.image === 'string' && job.image.trim() !== "") ? job.image : "/default.jpg"}
                  alt={job.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                />
                {/* Date overlay badge */}
                <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/50 backdrop-blur-sm text-white text-[11px] font-semibold">
                  <svg className="w-3 h-3 flex-shrink-0 opacity-80" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  {job.date}
                </span>
              </div>

              <div className="p-5 flex-1 flex flex-col">
                <h2 className="text-lg font-bold text-gray-800 line-clamp-2 mb-4">
                  {job.title}
                </h2>

                <div className="mt-auto pt-4 border-t border-gray-100">
                  {/* Usage instruction */}
                  <div className="flex items-start gap-1.5 px-2 py-1.5 rounded-md bg-amber-50 border border-amber-100 text-[10px] mb-3">
                    <svg className="w-3 h-3 text-amber-700 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    <span><span className="font-semibold text-amber-800 uppercase tracking-wide">{t('usage_label')}: </span><span className="text-amber-900">{t('pdf_viewer_instruction')}</span></span>
                  </div>
                  {/* Size + View PDF row */}
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-50 border border-gray-200 text-[10px]">
                      <svg className="w-3 h-3 text-gray-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 7v10c0 2 1 3 3 3h10c2 0 3-1 3-3V7" /><path strokeLinecap="round" strokeLinejoin="round" d="M9 11h6M9 15h4" /></svg>
                      <span className="font-semibold text-gray-700 uppercase tracking-wide">{t('file_size_label')}:</span>
                      <span className="font-medium text-gray-700">{job.file_size || "~1–5 MB"}</span>
                    </span>
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href={job.free_pdf}
                      className="inline-flex items-center px-4 py-1.5 rounded-full bg-primary-50 text-primary-700 text-xs font-bold hover:bg-primary-600 hover:text-white transition duration-300 border border-primary-100"
                      aria-label={`${t('viewPDF')} for ${job.title}`}
                    >
                      {t('viewPDF')}
                    </a>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Pagination Section */}
        {data.length > 0 && (
          <div className="flex flex-col items-center gap-6 border-t border-gray-100">
            <div className="flex items-center justify-center gap-4">
              <button
                className="px-6 py-2.5 rounded-xl bg-white border border-[#dadce0] text-gray-700 font-bold text-sm hover:bg-primary-50 hover:text-primary-600 hover:border-primary-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-gray-700 disabled:hover:border-[#dadce0] transition duration-300 flex items-center gap-2 shadow-sm"
                disabled={pagination.current_page === 1}
                onClick={() => fetchData(pagination.current_page - 1)}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                {t('previous')}
              </button>

              <div className="flex items-center bg-gray-50 border border-[#dadce0] rounded-xl px-4 py-2 font-bold text-sm tracking-tight shadow-inner">
                <span className="text-gray-600 uppercase text-[10px] mr-2 tracking-widest">{t('page')}</span>
                <span className="text-primary-600 px-1">{pagination.current_page}</span>
                <span className="text-gray-600 mx-1">/</span>
                <span className="text-gray-800">{pagination.last_page}</span>
              </div>

              <button
                className="px-6 py-2.5 rounded-xl bg-white border border-[#dadce0] text-gray-700 font-bold text-sm hover:bg-primary-50 hover:text-primary-600 hover:border-primary-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-gray-700 disabled:hover:border-[#dadce0] transition duration-300 flex items-center gap-2 shadow-sm"
                disabled={pagination.current_page === pagination.last_page}
                onClick={() => fetchData(pagination.current_page + 1)}
              >
                {t('next')}
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
              </button>
            </div>


          </div>
        )}
      </div>
    </section>
  );
}



